/**
 * WelcomePage — one-time, skippable post-signup step (#496):
 * "What should we call you?" plus a solo/team fork. Solo saves the
 * name and lands on the Dashboard; Team saves the name, then hands off
 * to the existing Create Workspace flow (/workspaces/new), which
 * creates the org_ tenant, sends optional invites, and switches in.
 * The seen-flag is written before any navigation so the step is
 * refresh-safe and never re-shows.
 */

import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Users } from 'lucide-react';
import { FULL_NAME_MAX_LENGTH } from '@puglieseweb/shared-react-auth';
import { Spinner } from '@systemdox/ui';
import { useAuthContext } from '@/contexts/AuthContext';
import { markWelcomeSeen } from '@/utils/welcome';
import { saveWelcomeName } from '@/lib/welcomeName';

const NAME_ERROR_ID = 'welcome-name-error';

export default function WelcomePage() {
  const { user, updateUserAttributes } = useAuthContext();
  const navigate = useNavigate();
  const [name, setName] = useState(user?.fullName ?? '');
  const [saving, setSaving] = useState<'solo' | 'team' | null>(null);
  /** shared-auth refused the name: its rule's sentence, until the name changes. */
  const [nameError, setNameError] = useState<string | null>(null);
  const nameInput = useRef<HTMLInputElement>(null);

  const finish = async (path: 'solo' | 'team') => {
    if (saving) return;
    setSaving(path);
    setNameError(null);
    if (user?.userId) markWelcomeSeen(user.userId);
    // A name the rule refuses is said, and the step stays so it can be
    // corrected (or skipped). Any other failure is best-effort, as before:
    // never block the user's first session on the name (lib/welcomeName).
    const outcome = await saveWelcomeName(updateUserAttributes, name, user?.fullName);
    if (outcome.kind === 'refused') {
      setNameError(outcome.sentence);
      setSaving(null);
      nameInput.current?.focus();
      return;
    }
    navigate(path === 'team' ? '/workspaces/new' : '/', { replace: true });
  };

  const skip = () => {
    if (user?.userId) markWelcomeSeen(user.userId);
    navigate('/', { replace: true });
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950 px-4">
      <div className="w-full max-w-md rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-8 space-y-6">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">
            Welcome to SystemDox
          </h1>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
            Two quick questions and you're in.
          </p>
        </div>

        <div>
          <label
            htmlFor="welcome-name"
            className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300"
          >
            What should we call you?
          </label>
          <input
            id="welcome-name"
            ref={nameInput}
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setNameError(null);
            }}
            maxLength={FULL_NAME_MAX_LENGTH}
            placeholder="Your name"
            autoFocus
            aria-invalid={nameError ? true : undefined}
            aria-describedby={nameError ? NAME_ERROR_ID : undefined}
            className={`w-full rounded-md border ${nameError ? 'border-red-500 dark:border-red-400' : 'border-slate-300 dark:border-slate-700'} bg-white dark:bg-slate-900 px-3 py-2 text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400 focus:border-purple-500 focus:outline-none`}
          />
          {nameError && (
            <p id={NAME_ERROR_ID} role="alert" className="mt-1.5 text-xs text-red-600 dark:text-red-400">
              {nameError}
            </p>
          )}
        </div>

        <div>
          <p className="mb-2 text-sm font-medium text-slate-700 dark:text-slate-300">
            Working solo or with a team?
          </p>
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => finish('solo')}
              disabled={Boolean(saving)}
              className="flex flex-col items-center gap-2 rounded-lg border border-slate-200 dark:border-slate-700 p-4 text-sm font-medium text-slate-700 dark:text-slate-300 hover:border-purple-400 hover:bg-purple-50/50 dark:hover:bg-purple-950/30 disabled:opacity-60"
            >
              {saving === 'solo' ? (
                <Spinner size="md" tone="current" className="text-purple-500" />
              ) : (
                <User className="w-6 h-6 text-purple-500" />
              )}
              Just me
              <span className="text-xs font-normal text-slate-400">
                Start in your personal workspace
              </span>
            </button>
            <button
              type="button"
              onClick={() => finish('team')}
              disabled={Boolean(saving)}
              className="flex flex-col items-center gap-2 rounded-lg border border-slate-200 dark:border-slate-700 p-4 text-sm font-medium text-slate-700 dark:text-slate-300 hover:border-purple-400 hover:bg-purple-50/50 dark:hover:bg-purple-950/30 disabled:opacity-60"
            >
              {saving === 'team' ? (
                <Spinner size="md" tone="current" className="text-purple-500" />
              ) : (
                <Users className="w-6 h-6 text-purple-500" />
              )}
              With a team
              <span className="text-xs font-normal text-slate-400">
                Create a shared workspace and invite people
              </span>
            </button>
          </div>
        </div>

        <p className="text-center">
          <button
            type="button"
            onClick={skip}
            className="text-sm text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
          >
            Skip for now
          </button>
        </p>
      </div>
    </div>
  );
}
